import { getZoneForPoint, GetDistance } from './GeoFunctions';

export const FareCalculator = (distance, time, rateDetails, instructionData, decimal) => {

    // --- Options Calculation (Seats, etc) ---
    let optionsTotal = 0;
    if (instructionData && instructionData.options && Array.isArray(instructionData.options)) {
        instructionData.options.forEach(option => {
            const priceKey = `${option.key}_price`;
            if (rateDetails[priceKey]) {
                optionsTotal += (parseFloat(rateDetails[priceKey]) * parseInt(option.count));
            }
        });
    }

    // --- CASE 0: HOURLY BOOKING (Mise à disposition) ---
    // specific trip type, distinct from "A to B" logic
    if (instructionData && instructionData.tripType === 'hourly') {
        const hours = parseFloat(instructionData.hours) || parseFloat(instructionData.options?.hours) || 1;
        let hourlyTotal = 0;

        // Priority 1: Pre-configured hourly packages in Admin UI (2h à 15h)
        const pkg = (rateDetails.hourly_packages || []).find((p) => parseFloat(p.hours) === hours);
        if (pkg) {
            hourlyTotal = parseFloat(pkg.price);
        } else {
            // Priority 2: Standard Hourly Rate
            const hourlyRate = parseFloat(
                (rateDetails.rate_per_hour !== undefined && rateDetails.rate_per_hour !== null && rateDetails.rate_per_hour !== "")
                    ? rateDetails.rate_per_hour
                    : (rateDetails.hourly_rate || 0)
            );
            hourlyTotal = (hourlyRate * hours);
        }

        if (rateDetails.base_fare > 0) {
            hourlyTotal += parseFloat(rateDetails.base_fare);
        }

        if (instructionData.waitingTime && instructionData.waitingTime > 0 && rateDetails.waiting_time_price) {
            const wPrice = (parseFloat(rateDetails.waiting_time_price) * parseFloat(instructionData.waitingTime));
            hourlyTotal += wPrice;
            // Note: optionsTotal is also updated here, but it's not used in the return for hourlyTotal
            // The original code added wPrice to optionsTotal, but then hourlyTotal += optionsTotal.
            // This means waiting time price was effectively added twice if optionsTotal was already non-zero.
            // For hourly, waiting time is part of the hourlyTotal, not an 'option'.
            // Let's keep it simple and just add to hourlyTotal.
        }

        hourlyTotal += optionsTotal; // Add options to the final hourly total

        return {
            totalCost: parseFloat(hourlyTotal.toFixed(decimal)),
            grandTotal: parseFloat(hourlyTotal.toFixed(decimal)),
            convenience_fees: 0,
            optionsTotal: parseFloat(optionsTotal.toFixed(decimal))
        };
    }

    // --- Helper to normalize coordinates ---
    const normalizeCoords = (point) => {
        if (!point) return null;
        let lat = point.lat || point.latitude;
        let lng = point.lng || point.longitude;

        // Handle Google Maps geometry object
        if (typeof lat === 'function') lat = lat();
        if (typeof lng === 'function') lng = lng();

        // Handle nested coords object
        if (!lat && point.coords) {
            lat = point.coords.lat || point.coords.latitude;
            lng = point.coords.lng || point.coords.longitude;
        }

        // Handle Map region (latitudeDelta etc) -> Just take center
        // If still null, try parsing if string (rare)

        if (lat !== undefined && lng !== undefined) {
            return { lat: parseFloat(lat), lng: parseFloat(lng) };
        }
        return null;
    };

    const pickupPoint = normalizeCoords(instructionData.pickup);
    const dropoffPoint = normalizeCoords(instructionData.dropoff);

    // ============================================================
    //  PRIORITY 1: ZONE -> ZONE PRICING (Forfaits zone à zone)
    // ============================================================

    // Validate normalized points exist before proceeding
    if (instructionData && instructionData.zones && pickupPoint && dropoffPoint && rateDetails.zoneToZonePrices && rateDetails.zoneToZonePrices.length > 0) {

        // DEBUG: Inspect Zone Structure
        const zoneKeys = Object.keys(instructionData.zones);
        if (zoneKeys.length > 0) {
            const firstZone = instructionData.zones[zoneKeys[0]];
        }

        const pZone = getZoneForPoint(pickupPoint, instructionData.zones);
        const dZone = getZoneForPoint(dropoffPoint, instructionData.zones);



        if (pZone && dZone) {
            // MATCHING BY ID (KEY) IS CRITICAL HERE
            // The Expert Advisor confirmed we must match r.from/r.to (which are keys) with zone.id (which acts as key)
            const rule = rateDetails.zoneToZonePrices.find(r =>
                (r.from === pZone.id && r.to === dZone.id) ||
                (r.isSymmetrical && r.from === dZone.id && r.to === pZone.id)
            );

            if (rule) {
                let fixedPrice = instructionData.isNight && rule.nightPrice ? parseFloat(rule.nightPrice) : parseFloat(rule.price);

                if (instructionData.isRoundTrip) {
                    fixedPrice = fixedPrice * 2;
                    if (rateDetails.return_discount && rateDetails.return_discount > 0) {
                        fixedPrice = fixedPrice - (fixedPrice * parseFloat(rateDetails.return_discount) / 100);
                    }
                }

                // Add options total to the fixed price
                fixedPrice += optionsTotal;

                return {
                    totalCost: parseFloat(fixedPrice.toFixed(decimal)),
                    grandTotal: parseFloat(fixedPrice.toFixed(decimal)),
                    convenience_fees: 0,
                    optionsTotal: parseFloat(optionsTotal.toFixed(decimal))
                };
            }
        }
    }

    // ============================================================
    //  PRIORITY 2: FIXED ROUTE (Trajets fixes)
    // ============================================================

    if (instructionData && instructionData.customRoutes && pickupPoint && dropoffPoint) {
        const TOLERANCE_KM = 2.0; // Increased to 2km for Airports

        // Check for matching route
        const routeMatch = Object.values(instructionData.customRoutes).find(r => {
            if (r.active && r.vehiclePrices && r.vehiclePrices[rateDetails.name]) {
                const rPickup = normalizeCoords(r.pickup);
                const rDropoff = normalizeCoords(r.dropoff);

                if (!rPickup || !rDropoff) return false;

                const distA = GetDistance(pickupPoint.lat, pickupPoint.lng, rPickup.lat, rPickup.lng);
                const distB = GetDistance(dropoffPoint.lat, dropoffPoint.lng, rDropoff.lat, rDropoff.lng);
                // Check A->B
                if (distA < TOLERANCE_KM && distB < TOLERANCE_KM) return true;
                return false;
            }
            return false;
        });

        console.log("Route Match Result:", routeMatch);

        if (routeMatch) {
            let fixedPrice = parseFloat(routeMatch.vehiclePrices[rateDetails.name]);

            // Round Trip Logic for Fixed Price
            if (instructionData.isRoundTrip) {
                fixedPrice = fixedPrice * 2;
                if (rateDetails.return_discount > 0) {
                    fixedPrice = fixedPrice - (fixedPrice * rateDetails.return_discount / 100);
                }
            }

            fixedPrice += optionsTotal;

            return {
                totalCost: parseFloat(fixedPrice.toFixed(decimal)),
                grandTotal: parseFloat(fixedPrice.toFixed(decimal)),
                convenience_fees: 0,
                optionsTotal: parseFloat(optionsTotal.toFixed(decimal))
            };
        }
    }

    // ============================================================
    //  PRIORITY 3: STANDARD PRICING (Distance + Time)
    //  Now supports distance_tiers for tiered km rates
    // ============================================================

    // Helper: Calculate distance cost using tiered pricing
    const calculateTieredDistanceCost = (distanceKm, tiers, isRoundTrip) => {
        if (!tiers || tiers.length === 0) {
            return null; // No tiers defined, will fallback to standard rate
        }

        // Sort tiers by maxDistance ascending
        const sortedTiers = [...tiers].sort((a, b) =>
            parseFloat(a.maxDistance) - parseFloat(b.maxDistance)
        );

        let applicableTier = null;

        // Find the tier that matches the distance
        for (const tier of sortedTiers) {
            if (distanceKm <= parseFloat(tier.maxDistance)) {
                applicableTier = tier;
                break;
            }
        }

        // If distance exceeds all tiers, use the last (highest) tier
        if (!applicableTier && sortedTiers.length > 0) {
            applicableTier = sortedTiers[sortedTiers.length - 1];
        }

        if (!applicableTier) return null;

        // Use returnRate for round trips, oneWayRate otherwise
        const ratePerKm = isRoundTrip
            ? parseFloat(applicableTier.returnRate || applicableTier.oneWayRate || 0)
            : parseFloat(applicableTier.oneWayRate || 0);

        return ratePerKm * distanceKm;
    };

    // Try tiered pricing first
    const tieredCost = calculateTieredDistanceCost(
        parseFloat(distance),
        rateDetails.distance_tiers,
        instructionData?.isRoundTrip || instructionData?.isReturn
    );

    let baseCalculated;

    if (tieredCost !== null) {
        // Use tiered distance pricing + time component
        baseCalculated = tieredCost +
            parseFloat(rateDetails.rate_per_hour || 0) * (parseFloat(time) / 3600);
        console.log(`[FareCalculator] Using tiered pricing. Distance: ${distance}km, Cost: ${tieredCost}`);
    } else {
        // Fallback to standard flat rate
        baseCalculated =
            parseFloat(rateDetails.rate_per_unit_distance || 0) * parseFloat(distance) +
            parseFloat(rateDetails.rate_per_hour || 0) * (parseFloat(time) / 3600);
    }

    if (rateDetails.base_fare > 0) {
        baseCalculated += parseFloat(rateDetails.base_fare);
    }

    // Legacy support
    if (instructionData && instructionData.optionSelected) {
        baseCalculated += instructionData.optionSelected.amount;
    }
    if (instructionData && instructionData.parcelTypeSelected) {
        baseCalculated += instructionData.parcelTypeSelected.amount;
    }

    baseCalculated += optionsTotal;

    // --- Night Surge ---
    if (instructionData && instructionData.isNight && rateDetails.night_surge_details) {
        const { type, value } = rateDetails.night_surge_details;
        if (type === 'flat') {
            baseCalculated += parseFloat(value);
        } else {
            baseCalculated = baseCalculated + (baseCalculated * parseFloat(value) / 100);
        }
    }

    // --- Round Trip Logic ---
    if (instructionData && instructionData.isRoundTrip) {
        let roundTripCost = baseCalculated * 2;

        // Apply discount
        if (rateDetails.return_discount) {
            const discount = parseFloat(rateDetails.return_discount);
            if (discount > 0) {
                roundTripCost = roundTripCost - (roundTripCost * discount / 100);
            }
        }

        baseCalculated = roundTripCost;
    }

    // --- Convenience Fees ---
    let total = baseCalculated > parseFloat(rateDetails.min_fare) ? baseCalculated : parseFloat(rateDetails.min_fare);
    let convenienceFee = 0;
    if (rateDetails.convenience_fee_type && rateDetails.convenience_fee_type == 'flat') {
        convenienceFee = rateDetails.convenience_fees;
    } else {
        convenienceFee = (total * parseFloat(rateDetails.convenience_fees) / 100);
    }
    let grand = total + convenienceFee;

    // --- Waiting Time (Extra) ---
    if (instructionData && instructionData.waitingTime && instructionData.waitingTime > 0) {
        const airportFree = rateDetails.waiting_time_start_airport ? parseFloat(rateDetails.waiting_time_start_airport) : 30;
        const otherFree = rateDetails.waiting_time_start ? parseFloat(rateDetails.waiting_time_start) : 15;
        const freeMinutes = instructionData.isAirport ? airportFree : otherFree;
        const chargeableMinutes = Math.max(0, parseFloat(instructionData.waitingTime) - freeMinutes);

        if (chargeableMinutes > 0) {
            const pricePerMin = parseFloat(rateDetails.waiting_price_per_min || rateDetails.waiting_time_price || 0);
            if (pricePerMin > 0) {
                grand += (pricePerMin * chargeableMinutes);
            }
        }
    }

    return {
        totalCost: parseFloat(total.toFixed(decimal)),
        grandTotal: parseFloat(grand.toFixed(decimal)),
        convenience_fees: parseFloat(convenienceFee.toFixed(decimal)),
        optionsTotal: parseFloat(optionsTotal.toFixed(decimal))
    }
}
